import { SCAN_AVERAGE_KEY_GAP_MS } from '@/lib/barcode-scan'
import { isKeyForFocusedControl } from '@/lib/page-keyboard'
import { type RefObject, useEffect } from 'react'

/**
 * "/" anywhere on the page puts the cursor in its search box, ready to replace what's there.
 * Typing in a field, a key while a dialog is open, and a "/" in a barcode scan are left alone.
 */
export function usePageSearchShortcut(inputRef: RefObject<HTMLInputElement | null>): void {
  useEffect(() => {
    let lastKeyAt = Number.NEGATIVE_INFINITY
    let pendingFocus: ReturnType<typeof setTimeout> | null = null

    const cancelPendingFocus = () => {
      if (pendingFocus) {
        clearTimeout(pendingFocus)
        pendingFocus = null
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Keys this close together are a scanner typing a code, which may contain a "/".
      const isScannerSpeed = event.timeStamp - lastKeyAt <= SCAN_AVERAGE_KEY_GAP_MS
      lastKeyAt = event.timeStamp

      if (isScannerSpeed) {
        // The "/" was the first character of a scan, which the page scanner is reading.
        cancelPendingFocus()
        return
      }

      if (
        event.key !== '/' ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.repeat ||
        isKeyForFocusedControl(event)
      ) {
        return
      }

      event.preventDefault()
      cancelPendingFocus()
      // A scanner sends its next key within this gap; a person pressing "/" won't notice it.
      pendingFocus = setTimeout(() => {
        pendingFocus = null
        inputRef.current?.focus()
        inputRef.current?.select()
      }, SCAN_AVERAGE_KEY_GAP_MS)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      cancelPendingFocus()
    }
  }, [inputRef])
}
